import { Text, View } from '@react-pdf/renderer';
import { formatPrice } from '@weft/services';
import type { CommitmentTierUsageData, Currency, PerUnitPricingConfiguration } from '@weft/types';
import { formatNumber } from '@weft/utils';
import React from 'react';
import { calculateCommitmentUnitsAmount } from '../../../../light-invoice-components/commitment-calculator';

type CommitmentProductLineItemPerUnitProps = {
  productVariantId: string;
  pricingConfiguration: PerUnitPricingConfiguration;
  tierUsageMap: Record<string, CommitmentTierUsageData[]>;
  currency: Currency
};

export const CommitmentProductLineItemPerUnit: React.FC<CommitmentProductLineItemPerUnitProps> = ({
  productVariantId, tierUsageMap, pricingConfiguration, currency,
}) => {
  if (isCommitmentNotCrossed(tierUsageMap, productVariantId)) {
    const units = calculateCommitmentUnitsAmount(tierUsageMap, productVariantId);

    return (
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          fontSize: '16px',
          fontWeight: 400,
          color: '#A3ACBD',
        }}
      >
        <Text>Commitment non billable</Text>
        <Text>{`${formatNumber(units)} X ${formatPrice(0, currency)}`}</Text>
      </View>
    );
  }

  if (isCommitmentCrossed(tierUsageMap, productVariantId)) {
    const units = tierUsageMap[productVariantId][0].billableUnits;
    const price = pricingConfiguration.price;

    return (
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          fontSize: '12px',
          fontWeight: 400,
          color: '#A3ACBD',
        }}
      >
        <Text>{`${formatNumber(units)} X ${formatPrice(price, currency)}`}</Text>
      </View>
    );
  }

  const price = pricingConfiguration.price;
  const units = tierUsageMap[productVariantId][0].billableUnits;
  const nonBillableUnits = calculateCommitmentUnitsAmount(tierUsageMap, productVariantId);

  return (
    <React.Fragment>
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          fontSize: '16px',
          fontWeight: 400,
          color: '#A3ACBD',
        }}
      >
        <Text>{`${formatNumber(units)} X ${formatPrice(price, currency)}`}</Text>
      </View>

      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          fontSize: '16px',
          fontWeight: 400,
          color: '#A3ACBD',
        }}
      >
        <Text>Commitment non billable</Text>
        <Text>{`${formatNumber(nonBillableUnits)} X ${formatPrice(0, currency)}`}</Text>
      </View>
    </React.Fragment>
  );
};

function isCommitmentNotCrossed(tierUsageMap: Record<string, CommitmentTierUsageData[]>, productVariantId: string) {
  const variantUsageData = tierUsageMap[productVariantId];

  const singleTierUsageData = variantUsageData[0];

  return singleTierUsageData.billableUnits === 0;
}

function isCommitmentCrossed(tierUsageMap: Record<string, CommitmentTierUsageData[]>, productVariantId: string) {
  const commitmentUnitsAmount = calculateCommitmentUnitsAmount(tierUsageMap, productVariantId);

  return commitmentUnitsAmount === 0;
}